import React from "react";
import T from "prop-types";
import { get } from "lodash";
import { INCOMING_LINE_ITEMS } from "MODULES/IncomingQuotes/constants";
import { CREDIT_CARD, WIRE_IN_ADVANCE } from "MODULES/companies/constants";
import Warning from "MODULES/shared/components/Warning/Warning";
import { OTHER } from "MODULES/shared/constants";
import {
  COMPANY,
  DROPSHIP
} from "MODULES/shared/components/ShippingSection/shippingTypes";

export function InStockWarning() {
  return (
    <Warning>
      Sales Order contains parts that are in stock and repairing. Please ensure
      all items are in the same state in your cart before proceeding.
    </Warning>
  );
}

export function StoreLocationWarning() {
  return (
    <Warning>
      Sales order contains items from different store locations. Please ensure
      all items in your cart belong to the same store location and owner before
      proceeding.
    </Warning>
  );
}

export function StoreOwnerWarning() {
  return (
    <Warning>
      Sales order contains items from different store owners. Please ensure all
      items in your cart belong to the same store location and owner before
      proceeding.
    </Warning>
  );
}

export function allRepairingOrAllInStock(inventories) {
  return (
    inventories.every(i => i.repairing) || inventories.every(i => !i.repairing)
  );
}

export function NoPmaNoDerWarning({ company, inventories = [] }) {
  if (
    (get(company, "no_pmas_accepted") || get(company, "no_ders_accepted")) &&
    inventories.some(i => i.pma || i.der)
  ) {
    return (
      <Warning>
        Warning: This Customer does not usually accept PMA/DER Parts.
      </Warning>
    );
  }

  return null;
}

NoPmaNoDerWarning.propTypes = {
  company: T.shape({
    no_ders_accepted: T.bool,
    no_pmas_accepted: T.bool
  }),
  inventories: T.arrayOf(
    T.shape({
      pma: T.bool,
      der: T.bool
    })
  )
};

export function TraceTypeWarning({ company, inventories }) {
  if (!get(company, "unaccepted_trace_types")) return null;

  if (
    inventories.some(i => company.unaccepted_trace_types.includes(i.trace_type))
  ) {
    const traceTypes = inventories.map(i => i.trace_type);
    return (
      <Warning>
        Warning: This customer does not usually accept units with trace types of{" "}
        {company.unaccepted_trace_types
          .filter(t => traceTypes.includes(t))
          .map(t => INCOMING_LINE_ITEMS.traceTypes[t].label)
          .join(", ")}
      </Warning>
    );
  }

  return null;
}

TraceTypeWarning.propTypes = {
  company: T.shape({
    unaccepted_trace_types: T.arrayOf(T.string)
  }),
  inventories: T.arrayOf(T.shape({ trace_type: T.string }))
};

export function WarrantyMinimumWarning({ company, lineItems }) {
  if (!company) return null;

  const warranties = lineItems
    .map(l => {
      switch (l.warranty) {
        case "30 days":
          return 30;
        case "6 mos.":
          return 6 * 30;
        case "1 year":
          return 365;
        default:
          return null;
      }
    })
    .filter(l => l);

  if (warranties.length === 0) return null;

  const lowestWarranty = Math.min(...warranties);
  if (
    Number(company.warranty_minimum) > 0 &&
    lowestWarranty > 0 &&
    lowestWarranty < company.warranty_minimum
  ) {
    return (
      <Warning>
        This Customer does not usually accept units with warranty periods less
        than {company.warranty_minimum} days.
      </Warning>
    );
  }

  return null;
}

WarrantyMinimumWarning.propTypes = {
  company: T.shape({
    warranty_minimum: T.number
  }),
  lineItems: T.arrayOf(
    T.shape({
      warranty: T.string
    })
  )
};

export function CreditLimitWarning({ company, lineItems, customerCreditTerm }) {
  if (!company) return null;

  const balance = parseFloat(company.outstanding_balance);
  const creditLimit = parseFloat(company.customer_credit_limit || 0);

  const lineItemBalance = lineItems.reduce((acc, l) => {
    const val = l.exchange_sale
      ? parseFloat(l.exchange_fee || 0)
      : parseInt(l.quantity, 10) * parseFloat(l.price_per_unit || 0);
    return val + acc;
  }, 0);

  if (
    balance + lineItemBalance > creditLimit &&
    ![CREDIT_CARD, WIRE_IN_ADVANCE].includes(customerCreditTerm)
  ) {
    return (
      <Warning warningType="danger">
        Exceeds Credit Limit Notification: This order will put {company.name}{" "}
        over their Credit Limit and place the order on Accounting Hold.
        Continuing with this SO will send an `Exceeds Credit Limit` Notification
        to Accounting.
      </Warning>
    );
  }
  return null;
}

CreditLimitWarning.propTypes = {
  company: T.shape({
    customer_credit_limit: T.number,
    outstanding_balance: T.number
  }),
  lineItems: T.arrayOf(T.shape),
  customerCreditTerm: T.string
};

export function StrictTraceWarning({ company, inventories }) {
  const strictTraceRequired =
    get(company, "imperfect_trace") && inventories.some(i => i.imperfect_trace);
  if (!strictTraceRequired) return null;

  return (
    <Warning>
      {`Warning: This item is flagged as Imperfect Trace and ${company.name} has Imperfect Trace
      Requirements. Please confirm they'll accept or consider quoting another
      line item (if avail.)`}
    </Warning>
  );
}

StrictTraceWarning.propTypes = {
  company: T.shape({
    imperfect_trace: T.bool
  }),
  inventories: T.arrayOf(T.shape({ imperfect_trace: T.bool }))
};

export function BlanketExchangeAgreementWarning({ company, exchangeSale }) {
  if (get(company, "blanket_exchange_agreement") && exchangeSale) {
    return (
      <Warning>
        Blanket Exchange Agreement on file. Only create an Exchange Agreement if
        T&Cs are out of the ordinary for this sale.
      </Warning>
    );
  }

  return null;
}

BlanketExchangeAgreementWarning.propTypes = {
  company: T.shape({
    blanket_exchange_agreement: T.bool
  }),
  exchangeSale: T.bool
};

export function DiscreetWarning({ carrier, discreet, shippingType }) {
  if (discreet) return null;
  if (!shippingType && !carrier) return null;
  if (carrier && carrier !== OTHER) return null;
  if (shippingType && ![COMPANY, DROPSHIP].includes(shippingType)) return null;
  return (
    <Warning>
      Please confirm that this order is NOT meant to ship DISCREET
    </Warning>
  );
}

DiscreetWarning.propTypes = {
  carrier: T.string,
  discreet: T.bool,
  shippingType: T.string
};



// WEBPACK FOOTER //
// ./app/modules/SalesOrders/components/ConvertFromCartForm/Warnings.jsx